import {
  UserService,
  type UserSvcCreateInvitation,
  type UserSvcInvitationListItem,
  type UserSvcPaginationMeta,
} from '@kp/rest-api-javascript-sdk';

import type { ConditionalFilter, SortingFilter } from '../common';

export const UserInvitationStatus = {
  Pending: 'pending',
  Accepted: 'accepted',
} as const;
export type UserInvitationStatus =
  (typeof UserInvitationStatus)[keyof typeof UserInvitationStatus];

/** The languages an invitation e-mail can be sent in; mirrors the app locales. */
export const InvitationLanguage = {
  En: 'en',
  De: 'de',
  Nl: 'nl',
} as const;
export type InvitationLanguage =
  (typeof InvitationLanguage)[keyof typeof InvitationLanguage];

/** Filtering options accepted by the list endpoint. */
export const UserInvitationsOptions = {
  StatusPending: 'status:pending',
  StatusAccepted: 'status:accepted',
} as const;
export type UserInvitationsOptions =
  (typeof UserInvitationsOptions)[keyof typeof UserInvitationsOptions];

/** A tenant invitation as GET /user/api/invitations lists it. */
export type UserInvitation = UserSvcInvitationListItem;

/** Who sent an invitation; `null` once that user no longer exists. */
export type UserInvitationInviter = UserInvitation['invitedBy'];

export type UserInvitationSort = 'email' | 'createdAt';

export type UserInvitationsFilter = ConditionalFilter<UserInvitationsOptions> &
  SortingFilter<UserInvitationSort> & {
    /** Case-insensitive match on the invitee's email, first and last name. */
    search?: string;
  };

/** List the tenant's invitations. */
export const getUserInvitations = async (
  filter: UserInvitationsFilter = {},
): Promise<{ meta?: UserSvcPaginationMeta; data?: UserInvitation[] }> =>
  UserService.getInvitations({
    query: {
      skip: filter.skip,
      limit: filter.limit,
      filter: filter.filter,
      sort: filter.sort,
      sortDir: filter.sortDir,
      search: filter.search,
    },
  });

/**
 * Get details of a user invitation
 *
 * @param invitationId  The id of a user invitation that will be fetched
 */
export const getUserInvitation = (invitationId: string) =>
  UserService.getInvitationsByInvitationId({ path: { invitationId } });

/**
 * Create a new user invitation
 *
 * The AC's invite flow collects `email`, `roleIds` and `language`; the
 * name fields are accepted by the endpoint but not asked for.
 */
export const createUserInvitation = (invitation: UserSvcCreateInvitation) =>
  UserService.postInvitations({ body: invitation });

/**
 * Cancel a user invitation
 *
 * @param invitationId  The id of a user invitation that will be deleted
 */
export const deleteUserInvitation = (invitationId: string) =>
  UserService.deleteInvitationsByInvitationId({ path: { invitationId } });

/**
 * Re-send an invitation e-mail
 *
 * @param invitationId  The id of a user invitation that will be re-sent
 */
export const resendUserInvitation = (invitationId: string) =>
  UserService.postInvitationsByInvitationIdReminder({ path: { invitationId } });
